/**
 * Each building's crown and front door on the home screen (ADR-0026's last
 * step): a sign and an entrance that say what kind of place it is, in its own
 * materials.
 *
 * - **Office Tower:** a corporate sign on a steel frame with a logo, rooftop
 *   plant, a revolving door under a graphite canopy.
 * - **Grand Hotel:** a neon sign over a mansard roof, flickering the way old
 *   neon does; carriage lamps, a striped awning and a blade sign by the door.
 * - **Transit Centre:** a station clock (it tells the real time) above the
 *   glass vault, an enamel name board; an iron-arched way in to the trains.
 * - **Skyline Tower:** lit letters on a dark glass crown, a spire with a beacon,
 *   a helipad; a double-height glass lobby with its address on the wall.
 *
 * Drawn on the same 340-wide grid as the floors, with the façade's 14 units at
 * either edge. Scenery only, and quiet by the same rule as the street: every
 * colour is measured against the floor hues (`FACADE_ART`, places.spec.ts).
 */

const INK = '#3d2c1e';
const CREAM = '#fffaf0';
const GLOW = '#ffd27a';
const BRASS = '#c49a52';
const NEON_PINK = '#ff9ec7';
const line = `stroke="${INK}" stroke-width="2" stroke-linejoin="round"`;
const thin = `stroke="${INK}" stroke-width="1.5" stroke-linejoin="round"`;
const display = `font-family="Baloo 2, system-ui, sans-serif" font-weight="800"`;

/** A neon tube: a soft halo under a bright core, in one colour. */
function neonText(text: string, x: number, y: number, size: number, colour: string): string {
  const shape = `x="${x}" y="${y}" text-anchor="middle" ${display} font-size="${size}" letter-spacing="2" fill="none"`;
  return `<text ${shape} stroke="${colour}" stroke-width="4.5" stroke-opacity="0.35">${text}</text>
    <text ${shape} stroke="${colour}" stroke-width="1.4">${text}</text>`;
}

// ---------------------------------------------------------------- crowns (340 × 88)

const officeCrown = `
  <rect x="24" y="56" width="40" height="16" rx="2" fill="#d9d6d0" ${thin}/>
  <circle cx="44" cy="64" r="5" fill="#bdb9b1" ${thin}/><path d="M40 64 h8 M44 60 v8" stroke="${INK}" stroke-width="1"/>
  <path d="M282 72 V62 M304 72 V62" ${thin}/>
  <rect x="276" y="44" width="34" height="20" rx="3" fill="#bdb9b1" ${line}/>
  <path d="M300 10 V44" ${thin}/>
  <circle class="antenna-light" cx="300" cy="10" r="3" fill="${GLOW}" ${thin}/>
  <path d="M110 58 V72 M230 58 V72" stroke="#8a8279" stroke-width="3"/>
  <rect x="86" y="20" width="168" height="38" rx="4" fill="#2f3032" ${line}/>
  <rect x="102" y="44" width="6" height="7" fill="${BRASS}"/>
  <rect x="111" y="37" width="6" height="14" fill="${GLOW}"/>
  <rect x="120" y="30" width="6" height="21" fill="${BRASS}"/>
  <path d="M100 54 H128" stroke="${GLOW}" stroke-width="1.5"/>
  <text x="136" y="39" ${display} font-size="13" letter-spacing="2" fill="${CREAM}">OFFICE</text>
  <text x="137" y="52" ${display} font-size="9" letter-spacing="5.5" fill="${BRASS}">TOWER</text>
  <rect x="0" y="72" width="340" height="16" fill="#bdb9b1" ${line}/>
  <path d="M30 72 V88 M90 72 V88 M150 72 V88 M210 72 V88 M270 72 V88 M330 72 V88" stroke="#a9a59c" stroke-width="1"/>`;

const hotelCrown = `
  <path d="M300 10 V60" ${thin}/><path d="M300 12 l18 6 l-18 6 z" fill="#c98a8e" ${thin}/>
  <path d="M120 54 V62 M220 54 V62" ${line}/>
  <rect x="88" y="6" width="164" height="48" rx="6" fill="#2b2628" ${line}/>
  <g class="neon-hum">${neonText('GRAND', 154, 30, 19, NEON_PINK)}
    <path d="M214 14 l2.6 5.2 5.7 0.8 -4.1 4 1 5.7 -5.2 -2.7 -5.1 2.7 1 -5.7 -4.2 -4 5.8 -0.8 z" fill="none" stroke="${NEON_PINK}" stroke-width="1.3"/></g>
  <g class="neon-flicker">${neonText('HOTEL', 170, 48, 15, GLOW)}</g>
  <path d="M2 88 L22 60 L318 60 L338 88 Z" fill="#7a5a4a" ${line}/>
  <path d="M14 76 H326 M8 84 H332 M18 68 H322" stroke="#5e4434" stroke-width="1"/>
  <path d="M70 86 v-10 a7 7 0 0 1 14 0 v10 z M163 86 v-10 a7 7 0 0 1 14 0 v10 z M256 86 v-10 a7 7 0 0 1 14 0 v10 z" fill="#ffe2a6" ${thin}/>`;

/** The station clock's hands, at the hour it is where the player is. */
function clockHands(now: Date): string {
  const minutes = now.getMinutes();
  const hours = (now.getHours() % 12) + minutes / 60;
  const hand = (angle: number, length: number, width: number): string => {
    const a = (angle - 90) * (Math.PI / 180);
    const x = 170 + Math.cos(a) * length;
    const y = 32 + Math.sin(a) * length;
    return `<path d="M170 32 L${x.toFixed(1)} ${y.toFixed(1)}" stroke="${INK}" stroke-width="${width}" stroke-linecap="round"/>`;
  };
  return hand(hours * 30, 7, 2.2) + hand(minutes * 6, 10, 1.4);
}

function transitCrown(now: Date): string {
  const ticks = Array.from({ length: 12 }, (_, i) => {
    const a = (i * 30 * Math.PI) / 180;
    const r1 = i % 3 === 0 ? 10 : 11.5;
    return `M${(170 + Math.cos(a) * r1).toFixed(1)} ${(32 + Math.sin(a) * r1).toFixed(1)} L${(170 + Math.cos(a) * 13).toFixed(1)} ${(32 + Math.sin(a) * 13).toFixed(1)}`;
  }).join(' ');
  return `
  <path d="M150 56 V18 L170 6 L190 18 V56 Z" fill="#e2ddcf" ${line}/>
  <circle cx="170" cy="32" r="15" fill="${CREAM}" stroke="#3f5248" stroke-width="3"/>
  <circle cx="170" cy="32" r="16.5" fill="none" ${thin}/>
  <path d="${ticks}" stroke="${INK}" stroke-width="1"/>
  ${clockHands(now)}
  <circle cx="170" cy="32" r="1.6" fill="${INK}"/>
  <path d="M2 88 Q170 30 338 88 Z" fill="#d6e6ee" ${line}/>
  <path d="M60 76 Q170 44 280 76 M110 64 V88 M170 59 V88 M230 64 V88 M56 78 V88 M284 78 V88" fill="none" stroke="#3f5248" stroke-width="2"/>
  <rect x="92" y="64" width="156" height="20" rx="3" fill="#3f5248" ${line}/>
  <rect x="96" y="67" width="148" height="14" rx="2" fill="none" stroke="${CREAM}" stroke-width="0.8"/>
  <text x="170" y="78" text-anchor="middle" ${display} font-size="10.5" letter-spacing="2.2" fill="${CREAM}">TRANSIT CENTRE</text>`;
}

const skylineCrown = `
  <path d="M170 0 L176 40 H164 Z" fill="#b8c6d2" ${line}/>
  <circle class="beacon" cx="170" cy="4" r="3" fill="#ff8b74" ${thin}/>
  <rect x="96" y="38" width="148" height="24" fill="#1d2540" ${line}/>
  <path d="M100 58 H240" stroke="#7fd0e6" stroke-width="1" opacity="0.6"/>
  <text x="170" y="55" text-anchor="middle" ${display} font-size="14" letter-spacing="4" fill="${CREAM}" stroke="#7fd0e6" stroke-width="3" stroke-opacity="0.45" paint-order="stroke">SKYLINE</text>
  <rect x="20" y="62" width="300" height="26" fill="#d9e7f1" ${line}/>
  <path d="M50 62 V88 M80 62 V88 M110 62 V88 M140 62 V88 M170 62 V88 M200 62 V88 M230 62 V88 M260 62 V88 M290 62 V88" stroke="#9fb4c4" stroke-width="1.5"/>
  <ellipse cx="58" cy="62" rx="26" ry="4" fill="#8d9aa8" ${thin}/>
  <text x="58" y="64.5" text-anchor="middle" ${display} font-size="6" fill="${CREAM}">H</text>`;

/** The building's crown: its sign, its roof, the things that live up there. */
export function crownSvg(buildingId: string, now = new Date()): string {
  switch (buildingId) {
    case 'grand-hotel':
      return hotelCrown;
    case 'transit-centre':
      return transitCrown(now);
    case 'skyline-tower':
      return skylineCrown;
    default:
      return officeCrown;
  }
}

// ---------------------------------------------------------------- entrances (340 × 60)

const officeEntrance = `
  <rect x="146" y="6" width="158" height="9" fill="#2f3032" ${line}/>
  <text x="225" y="13" text-anchor="middle" ${display} font-size="6" letter-spacing="2" fill="${GLOW}">1 OFFICE TOWER</text>
  <rect x="160" y="15" width="130" height="45" fill="#d6e6ee" ${line}/>
  <path d="M186 15 V60 M264 15 V60" ${thin}/>
  <path d="M205 60 V30 a20 12 0 0 1 40 0 V60" fill="#c8dce8" ${thin}/>
  <path d="M225 21 V60 M211 28 L239 54 M239 28 L211 54" stroke="${INK}" stroke-width="1" opacity="0.6"/>
  <rect x="128" y="30" width="18" height="12" rx="1" fill="${BRASS}" stroke="${INK}" stroke-width="1"/>
  <path d="M131 34 h12 M131 38 h8" stroke="${INK}" stroke-width="0.8"/>
  <rect x="54" y="46" width="44" height="14" fill="#a8a49b" ${thin}/>
  <ellipse cx="76" cy="44" rx="22" ry="8" fill="#7fa874" ${thin}/>`;

const hotelEntrance = `
  <rect x="118" y="6" width="18" height="50" rx="3" fill="#2b2628" ${thin}/>
  <g class="neon-flicker slow">
    <text x="127" y="18" text-anchor="middle" ${display} font-size="8" fill="none" stroke="${NEON_PINK}" stroke-width="1.2">H</text>
    <text x="127" y="27" text-anchor="middle" ${display} font-size="8" fill="none" stroke="${NEON_PINK}" stroke-width="1.2">O</text>
    <text x="127" y="36" text-anchor="middle" ${display} font-size="8" fill="none" stroke="${NEON_PINK}" stroke-width="1.2">T</text>
    <text x="127" y="45" text-anchor="middle" ${display} font-size="8" fill="none" stroke="${NEON_PINK}" stroke-width="1.2">E</text>
    <text x="127" y="54" text-anchor="middle" ${display} font-size="8" fill="none" stroke="${NEON_PINK}" stroke-width="1.2">L</text>
  </g>
  <path d="M176 18 h108 l-8 -14 h-92 z" fill="#c98a8e" ${thin}/>
  <path d="M194 18 l3 -14 M212 18 l1 -14 M230 18 v-14 M248 18 l-1 -14 M266 18 l-3 -14" stroke="${CREAM}" stroke-width="3"/>
  <path d="M176 18 q6 5 12 0 q6 5 12 0 q6 5 12 0 q6 5 12 0 q6 5 12 0 q6 5 12 0 q6 5 12 0 q6 5 12 0 q6 5 12 0" fill="#c98a8e" ${thin}/>
  <rect x="198" y="24" width="64" height="36" fill="#94704f" ${line}/>
  <path d="M230 24 V60" ${thin}/>
  <rect x="204" y="28" width="20" height="12" rx="1" fill="#d6e6ee" stroke="${INK}" stroke-width="1"/>
  <rect x="236" y="28" width="20" height="12" rx="1" fill="#d6e6ee" stroke="${INK}" stroke-width="1"/>
  <circle cx="226" cy="44" r="1.6" fill="${BRASS}"/><circle cx="234" cy="44" r="1.6" fill="${BRASS}"/>
  <path d="M182 30 h8 M278 30 h-8" ${thin}/>
  <circle cx="182" cy="37" r="9" fill="${GLOW}" opacity="0.3"/><circle cx="278" cy="37" r="9" fill="${GLOW}" opacity="0.3"/>
  <rect x="178" y="31" width="8" height="12" rx="2" fill="${GLOW}" ${thin}/>
  <rect x="274" y="31" width="8" height="12" rx="2" fill="${GLOW}" ${thin}/>
  <path d="M70 60 l3 -16 h14 l3 16 z" fill="#c9774f" ${thin}/>
  <circle cx="80" cy="36" r="10" fill="#7fa874" ${thin}/>`;

const transitEntrance = `
  <rect x="96" y="12" width="66" height="26" rx="2" fill="#3f3a36" ${line}/>
  <text x="129" y="20" text-anchor="middle" font-family="system-ui, sans-serif" font-size="5" font-weight="800" letter-spacing="1" fill="${GLOW}">DEPARTURES</text>
  <path d="M101 25 h34 M101 30 h44 M101 35 h28 M148 25 h9 M148 30 h9 M148 35 h9" stroke="${GLOW}" stroke-width="1.6" stroke-linecap="round"/>
  <path d="M184 60 V26 A46 24 0 0 1 276 26 V60" fill="#d6e6ee" stroke="#3f5248" stroke-width="4"/>
  <path d="M184 26 H276 M230 6 V26 M200 12 L214 26 M260 12 L246 26" stroke="#3f5248" stroke-width="1.5"/>
  <rect x="202" y="32" width="56" height="28" fill="#c8dce8" ${thin}/>
  <path d="M230 32 V60" ${thin}/>
  <rect x="207" y="27" width="46" height="9" rx="2" fill="#3f5248" stroke="${INK}" stroke-width="1"/>
  <text x="230" y="34" text-anchor="middle" font-family="system-ui, sans-serif" font-size="6" font-weight="800" letter-spacing="1" fill="${CREAM}">TRAINS</text>
  <path d="M70 60 l3 -16 h14 l3 16 z" fill="#c9774f" ${thin}/>
  <circle cx="80" cy="36" r="10" fill="#7fa874" ${thin}/>`;

const skylineEntrance = `
  <rect x="152" y="4" width="148" height="56" fill="#d9e7f1" ${line}/>
  <path d="M177 4 V60 M202 4 V60 M250 4 V60 M275 4 V60 M152 30 H300" stroke="#9fb4c4" stroke-width="1.5"/>
  <rect x="142" y="26" width="168" height="3" fill="#8d9aa8" stroke="${INK}" stroke-width="1"/>
  <rect x="207" y="32" width="38" height="28" fill="#c8dce8" ${thin}/>
  <path d="M226 32 V60" ${thin}/>
  <text x="118" y="34" text-anchor="middle" ${display} font-size="18" letter-spacing="1" fill="#1d2540">88</text>
  <path d="M104 38 H132" stroke="#7fd0e6" stroke-width="2"/>
  <rect x="62" y="48" width="32" height="12" fill="#8d9aa8" ${thin}/>
  <circle cx="78" cy="34" r="11" fill="none" stroke="#8d9aa8" stroke-width="5"/>
  <circle cx="78" cy="34" r="11" fill="none" ${thin}/>`;

/** The way in: the building's own front door. */
export function entranceSvg(buildingId: string): string {
  switch (buildingId) {
    case 'grand-hotel':
      return hotelEntrance;
    case 'transit-centre':
      return transitEntrance;
    case 'skyline-tower':
      return skylineEntrance;
    default:
      return officeEntrance;
  }
}

/** Every crown and entrance, for the colour test. */
export const FACADE_ART = [
  officeCrown,
  hotelCrown,
  transitCrown(new Date(2026, 9, 15, 10, 10)),
  skylineCrown,
  officeEntrance,
  hotelEntrance,
  transitEntrance,
  skylineEntrance,
].join('\n');
